<template>
  <div @click.prevent.stop>
    <!-- Already in network -->
    <DsButton
      v-if="isInNetwork"
      icon="star"
      icon-fill-color="white"
      class="!mr-0"
      :label="$t('samergo_network_remove_from_wijk')"
      variant="rounded"
      size="small"
      @click="remove"
    />

    <!-- Not in network -->
    <DsButton
      v-else
      icon="star"
      icon-fill-color="white"
      class="!mr-0"
      :label="adding ? $t('samergo_network_adding') : $t('samergo_network_add_to_wijk')"
      variant="rounded"
      size="small"
      :disabled="adding"
      @click="handleAdd($event)"
    />

    <!-- Owner picker — teleported so overflow:hidden on parent cards can't clip it -->
    <Teleport to="body">
      <div
        v-if="pickerOpen && pickerPos"
        class="ncs-picker"
        :style="{ top: pickerPos.top + 'px', right: pickerPos.right + 'px' }"
        @click.stop
      >
        <!-- Header -->
        <div class="ncs-picker__header">
          <span class="material-symbols-outlined ncs-picker__header-icon">hub</span>
          <span class="ncs-picker__header-label">{{ $t('samergo_network_add_picker_title') }}</span>
        </div>

        <!-- Options -->
        <div class="ncs-picker__options">
          <button
            v-for="claim in myClaims"
            :key="claim.actor_id"
            class="ncs-picker__option"
            @click="addAs(claim.actor_id)"
          >
            <span class="ncs-picker__option-avatar">
              <img v-if="claim.logo" :src="claim.logo" class="ncs-picker__option-avatar-img"/>
              <span v-else class="material-symbols-outlined">business</span>
            </span>
            <span class="ncs-picker__option-name">{{ claim.name }}</span>
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { ACTION_TYPES } from '@/store/modules/network-contacts.js'
  import { fetchMyClaims } from '@/api/network-contacts'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'

  export default defineComponent({
    name: 'NetworkContactStar',
    props: {
      actorId: { type: String, required: true },
      actorName: { type: String, default: '' },
      actorImage: { type: String, default: '' },
    },
    data() {
      return {
        adding: false,
        pickerOpen: false,
        pickerPos: null as { top: number; right: number } | null,
        myClaims: [] as Array<{ actor_id: number; elastic_id: string; name: string; logo: string | null }>,
      }
    },
    created() {
      this.$store.dispatch(ACTION_TYPES.FETCH)
      this.loadClaims()
    },
    mounted() {
      document.addEventListener('click', this.closePicker)
    },
    beforeUnmount() {
      document.removeEventListener('click', this.closePicker)
    },
    computed: {
      isInNetwork(): boolean {
        return this.$store.getters.isNetworkContact(this.actorId)
      },
    },
    methods: {
      async loadClaims() {
        try {
          this.myClaims = await fetchMyClaims()
        } catch {
          // non-critical
        }
      },
      handleAdd(event: MouseEvent) {
        if (this.adding) return
        if (!this.myClaims.length) {
          // Users need an active claim before they can add actors to their social map
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { actorRequiredVariant: 'network' })
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ACTOR_CLAIM_REQUIRED)
          return
        }
        if (this.myClaims.length === 1) {
          this.addAs(this.myClaims[0].actor_id)
        } else {
          this.openPicker(event)
        }
      },
      openPicker(event: MouseEvent) {
        if (this.pickerOpen) { this.closePicker(); return }
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
        this.pickerPos = { top: rect.bottom + 8, right: window.innerWidth - rect.right }
        this.pickerOpen = true
      },
      closePicker() {
        this.pickerOpen = false
        this.pickerPos = null
      },
      async addAs(ownerActorId: number | null) {
        this.closePicker()
        if (this.adding || this.isInNetwork) return
        this.adding = true
        try {
          await this.$store.dispatch(ACTION_TYPES.ADD_CONTACT, {
            elasticId: this.actorId,
            name: this.actorName,
            image: this.actorImage,
            ownerActorId,
          })
        } finally {
          this.adding = false
        }
      },
      remove() {
        this.$store.dispatch(ACTION_TYPES.REMOVE_CONTACT, this.actorId)
      },
    },
  })
</script>

<style scoped lang="scss">
  .ncs-picker {
    position: fixed;
    z-index: 9999;
    background: white;
    border: 1px solid #e2e4ea;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(7, 28, 91, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
    min-width: 220px;
    overflow: hidden;
    font-family: 'Plus Jakarta Sans', Inter, sans-serif;
  }

  .ncs-picker__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px 12px;
    border-bottom: 1px solid #f0f1f5;
    background: #f8f9ff;
  }

  .ncs-picker__header-icon {
    font-size: 16px;
    color: #071c5b;
    opacity: 0.7;
  }

  .ncs-picker__header-label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #071c5b;
    letter-spacing: -0.01em;
  }

  .ncs-picker__options {
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .ncs-picker__option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    border: none;
    background: transparent;
    cursor: pointer;
    width: 100%;
    text-align: left;
    transition: background 0.12s;

    &:hover {
      background: #f3f4f8;
    }

    &:active {
      background: #e8eaf2;
    }
  }

  .ncs-picker__option-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: #071c5b;

    .material-symbols-outlined {
      font-size: 17px;
    }

    &--personal {
      background: #f0f1f5;
      color: #757681;
    }
  }

  .ncs-picker__option-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .ncs-picker__option-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #191c1d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
